/**
 * FASE 8.3 — <ArrayField> — array-of-objects schema-driven React component.
 *
 * Props:
 *   label:       string (titolo dell'array)
 *   value:       array of objects (attuale)
 *   itemSchema:  { fieldName: {type, label, placeholder?, required?} }
 *   maxItems:    number (default 5)
 *   minItems:    number (default 0)
 *   onChange:    (newArray) => void
 *   testId:      string prefix per data-testid
 */
import React from "react";
import { Plus, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

function ItemField({ field, name, value, onChange, testId }) {
  const {
    type = "text", label, placeholder = "", required = false,
  } = field;
  const commonProps = {
    "data-testid": testId,
    value: value ?? "",
    onChange: (e) => onChange(e.target?.value ?? e),
    placeholder,
    className: "h-10 rounded-lg border-[#E5E2DB]",
  };
  if (type === "textarea") {
    return (
      <div>
        <Label className="text-xs text-[#4A5E51] mb-1 block">
          {label}{required && <span className="text-red-600 ml-1">*</span>}
        </Label>
        <Textarea {...commonProps} rows={2}
                    className="rounded-lg border-[#E5E2DB] min-h-[60px]" />
      </div>
    );
  }
  if (type === "boolean") {
    return (
      <div className="flex items-center gap-3 py-2">
        <Label className="text-xs text-[#4A5E51] flex-1">
          {label}{required && <span className="text-red-600 ml-1">*</span>}
        </Label>
        <div className="flex gap-2">
          <button type="button" data-testid={`${testId}-yes`}
                    onClick={() => onChange(true)} aria-pressed={value === true}
                    className={`px-3 py-1 rounded-full text-xs font-medium border
                                  ${value === true
                                    ? "bg-[#163321] text-white border-[#163321]"
                                    : "bg-white text-[#163321] border-[#163321]/40"}`}>Sì</button>
          <button type="button" data-testid={`${testId}-no`}
                    onClick={() => onChange(false)} aria-pressed={value === false}
                    className={`px-3 py-1 rounded-full text-xs font-medium border
                                  ${value === false
                                    ? "bg-[#7A1D1D] text-white border-[#7A1D1D]"
                                    : "bg-white text-[#7A1D1D] border-[#7A1D1D]/40"}`}>No</button>
        </div>
      </div>
    );
  }
  return (
    <div>
      <Label className="text-xs text-[#4A5E51] mb-1 block">
        {label}{required && <span className="text-red-600 ml-1">*</span>}
      </Label>
      <Input type={type === "number" ? "number" : type === "date" ? "date" : "text"}
              {...commonProps} />
    </div>
  );
}

export function ArrayField({ label, value = [], itemSchema, maxItems = 5,
                             minItems = 0, onChange, testId = "array" }) {
  const items = Array.isArray(value) ? value : [];

  const addItem = () => {
    if (items.length >= maxItems) return;
    const newItem = Object.fromEntries(
      Object.entries(itemSchema).map(([k, f]) =>
        [k, f.type === "boolean" ? null : ""]));
    onChange([...items, newItem]);
  };
  const removeItem = (idx) => {
    if (items.length <= minItems) return;
    onChange(items.filter((_, i) => i !== idx));
  };
  const moveItem = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= items.length) return;
    const newArr = [...items];
    [newArr[idx], newArr[newIdx]] = [newArr[newIdx], newArr[idx]];
    onChange(newArr);
  };
  const updateItem = (idx, fieldKey, newValue) => {
    const newArr = items.map((it, i) =>
      i === idx ? { ...it, [fieldKey]: newValue } : it);
    onChange(newArr);
  };

  return (
    <div className="space-y-3" data-testid={`${testId}-container`}>
      {label && (
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium text-[#0F1F14]">{label}</Label>
          <span className="text-xs text-[#4A5E51]">
            {items.length}/{maxItems} elementi
          </span>
        </div>
      )}
      {items.length === 0 && (
        <div className="text-sm text-[#4A5E51] italic p-4 border border-dashed
                          border-[#D9C49A]/60 rounded-lg text-center bg-[#FDF8E8]/40"
              data-testid={`${testId}-empty`}>
          Nessun elemento aggiunto. Clicca &quot;Aggiungi&quot; per iniziare.
        </div>
      )}
      {items.map((item, idx) => (
        <div key={idx} className="border border-[#E5E2DB] rounded-xl p-4 bg-white"
              data-testid={`${testId}-item-${idx}`}>
          <div className="flex items-center justify-between mb-3 pb-2 border-b border-[#F2F1EC]">
            <span className="text-sm font-medium text-[#163321]">
              #{idx + 1}
            </span>
            <div className="flex gap-1">
              <button type="button"
                        onClick={() => moveItem(idx, -1)} disabled={idx === 0}
                        data-testid={`${testId}-move-up-${idx}`}
                        className="p-1 rounded hover:bg-slate-100 disabled:opacity-30">
                <ChevronUp className="w-4 h-4 text-[#4A5E51]" />
              </button>
              <button type="button"
                        onClick={() => moveItem(idx, 1)} disabled={idx === items.length - 1}
                        data-testid={`${testId}-move-down-${idx}`}
                        className="p-1 rounded hover:bg-slate-100 disabled:opacity-30">
                <ChevronDown className="w-4 h-4 text-[#4A5E51]" />
              </button>
              <button type="button"
                        onClick={() => removeItem(idx)} disabled={items.length <= minItems}
                        data-testid={`${testId}-remove-${idx}`}
                        className="p-1 rounded hover:bg-red-50 disabled:opacity-30">
                <Trash2 className="w-4 h-4 text-red-600" />
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {Object.entries(itemSchema).map(([fieldKey, field]) => (
              <div key={fieldKey}
                    className={field.type === "textarea" ? "sm:col-span-2" : ""}>
                <ItemField field={field} name={fieldKey}
                             value={item[fieldKey]}
                             onChange={(v) => updateItem(idx, fieldKey, v)}
                             testId={`${testId}-item-${idx}-${fieldKey}`} />
              </div>
            ))}
          </div>
        </div>
      ))}
      {items.length < maxItems && (
        <button type="button" onClick={addItem}
                  data-testid={`${testId}-add`}
                  className="w-full py-2.5 rounded-lg border-2 border-dashed
                              border-[#163321]/30 text-[#163321] hover:bg-[#163321]/5
                              inline-flex items-center justify-center gap-2 text-sm font-medium">
          <Plus className="w-4 h-4" /> Aggiungi elemento
        </button>
      )}
    </div>
  );
}


/**
 * <MultiPercentageField> — N slot % con vincolo somma=100.
 *
 * Props:
 *   label:      string
 *   slots:      [{key, label}] (N voci)
 *   value:      { key: number } (mapping key → %)
 *   onChange:   (newValue) => void
 *   testId:     data-testid prefix
 */
export function MultiPercentageField({ label, slots = [], value = {},
                                       onChange, testId = "multipct",
                                       requiredSum = 100 }) {
  const total = slots.reduce((s, sl) => s + (parseFloat(value[sl.key]) || 0), 0);
  const diff = requiredSum - total;
  const isValid = Math.abs(diff) < 0.01;

  const updateSlot = (key, val) => {
    const num = val === "" ? "" : Math.max(0, Math.min(100, parseFloat(val) || 0));
    onChange({ ...value, [key]: num });
  };

  return (
    <div className="space-y-3" data-testid={`${testId}-container`}>
      {label && (
        <Label className="text-sm font-medium text-[#0F1F14]">{label}</Label>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {slots.map((sl) => (
          <div key={sl.key} className="flex items-center gap-2">
            <Label className="text-sm text-[#4A5E51] flex-1">{sl.label}</Label>
            <div className="flex items-center gap-1">
              <Input type="number" min={0} max={100} step={1}
                      data-testid={`${testId}-slot-${sl.key}`}
                      value={value[sl.key] ?? ""}
                      onChange={(e) => updateSlot(sl.key, e.target.value)}
                      className="w-20 h-9 text-right rounded-lg border-[#E5E2DB]" />
              <span className="text-sm text-[#4A5E51]">%</span>
            </div>
          </div>
        ))}
      </div>
      <div className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm
                          ${isValid
                            ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
                            : "bg-red-50 border border-red-200 text-red-800"}`}
            data-testid={`${testId}-sum-status`}>
        <span>Somma attuale: <strong>{total.toFixed(2)}%</strong></span>
        {isValid
          ? <span className="text-xs">✓ Corretto</span>
          : <span className="text-xs">
              {diff > 0 ? `Manca ${diff.toFixed(2)}%` : `Superi di ${Math.abs(diff).toFixed(2)}%`}
            </span>}
      </div>
    </div>
  );
}
